import epicTracking from 'epic-tracking';
import {useMotionValue, useSpring, useTransform} from 'framer-motion';
import React from 'react';
import useSound from 'use-sound';
import root from 'window-or-global';

import {PlusIcon} from '../../../icons';

import HeroContainer from './HeroContainer';
import HeroImage from './HeroImage';
import IconButton from './IconButton';
import {HeroIds, ImageArray} from './Types';

interface Props {
  images: ImageArray[];
  active: boolean;
  keyId: string;
  mobile: boolean;
  tablet: boolean;
  sound?: string;
  goToSlide: (value: number) => void;
  index: number;
}

const Hero = ({images, active, keyId, mobile, tablet, sound, index, goToSlide}: Props) => {
  const [activeSkin, setSkin] = React.useState(0);
  const [currentSlideLength, setSlideLength] = React.useState(0);
  const [grootSound] = useSound(sound || '');
  const switchSkin = React.useCallback(() => {
    const max = images.length - 1;
    setSlideLength(max);
    if (sound && grootSound) {
      grootSound();
      console.clear();
      console.log('Turn on your sound and play again...');
    }
    if (activeSkin < max) setSkin((prev) => prev + 1);
    if (activeSkin >= max) {
      goToSlide(index + 1);
    }
  }, [images, setSkin, activeSkin, sound, grootSound]);

  // We only want to trigger the analytic event when a user directly engages with the button.
  const handleSwitchVariantClick = () => {
    const max = images.length - 1;
    epicTracking.trackEvent('Interaction', {
      interactionType: 'click',
      eventCategory: 'clickHeroSkin',
      eventAction: 'view-hero-skin',
      eventLabel: activeSkin + 1 > max ? 0 : activeSkin + 1,
    });

    switchSkin();
  };

  const xPos = useMotionValue(root ? root.innerWidth / 2 : 1000);
  const x = useTransform(xPos, [0, root ? root.innerWidth : 2000], [50, -50], {clamp: true});
  const xRotate = useTransform(xPos, [0, root ? root.innerWidth : 2000], [-10, 17], {
    clamp: true,
  });
  const xSpring = useSpring(x, {damping: 100});
  const rSpring = useSpring(xRotate, {damping: 100});

  const handleMouse = (e) => {
    const {clientX, pageX} = e;
    if (!pageX && clientX) xPos.set(clientX); //x position within the element.
    if (pageX) xPos.set(pageX);
  };

  React.useEffect(() => {
    root.addEventListener('mousemove', handleMouse, true);
    return () => {
      root.removeEventListener('mousemove', handleMouse, true);
    };
  }, [root]);

  React.useEffect(() => {
    const switchDelay = currentSlideLength ? 3000 : 4000;
    const rotateVariantInterval = setInterval(() => {
      switchSkin();
    }, switchDelay);

    return () => {
      clearInterval(rotateVariantInterval);
    };
  }, [switchSkin]);

  //special styles for certain heroes
  const yPos =
    HeroIds[keyId] === HeroIds.storm || (HeroIds[keyId] === HeroIds.groot && !mobile && !tablet)
      ? -65
      : 0;
  const scale = HeroIds[keyId] === HeroIds.groot ? 1.1 : 1;
  const xp = HeroIds[keyId] === HeroIds.thor ? -60 : 0;

  return (
    <HeroContainer
      initial={{
        scale,
        opacity: 0,
      }}
      style={{
        rotateY: active && !mobile && !tablet ? rSpring : 0,
        x: active && !mobile && !tablet ? xSpring : 0,
      }}
      animate={active ? {y: yPos, scale, opacity: 1} : {y: yPos, scale, opacity: 0}}
      transition={{opacity: {duration: 0.8, ease: 'easeInOut', delay: active ? 0.8 : 0}}}>
      {images.map((item, index) => {
        const itemOnDisplay = Boolean(index === activeSkin);

        return (
          <React.Fragment key={`${keyId}-${index}`}>
            <HeroImage
              active={itemOnDisplay}
              src={`${item.image}?width=500`}
              alt={item.imageAlt}
              initial={{x: 150, z: -200}}
              animate={
                active
                  ? {x: xp, z: 1}
                  : {
                      x: !mobile && !tablet ? 150 : 20,
                      z: !mobile && !tablet ? -200 : -50,
                    }
              }
              transition={{duration: 0.8, ease: 'easeInOut', delay: active ? 0.8 : 0}}
            />
            {(images.length > 1 || sound) && itemOnDisplay && (
              <IconButton keyId={keyId} onClick={handleSwitchVariantClick}>
                <PlusIcon fillColor={item.plusIconColor} />
              </IconButton>
            )}
          </React.Fragment>
        );
      })}
    </HeroContainer>
  );
};

export default Hero;
